/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useCallback, useEffect, useState, useContext } from 'react';
import {
  deleteFromSessionStorage,
  EMAIL_CONFIRMATION_ERROR,
  EMAIL_CONFIRMED_SUCCESS,
  ensureValidUri,
  ERROR_EMAIL_CONFIRMATION_MESSAGE,
  flattenObjectWithChar,
  getNumberFromSessionStorage,
  getSSOUrl,
  hasValue,
  INVALID_AUTHORIZATION_CODE_ERROR_MESSAGE,
  isNullOrUndefined,
  setNumberInSessionStorage,
  SUCCESS_EMAIL_CONFIRMED_MESSAGE,
} from '@r-and-a-shared-ui/utils';
import FormStep, { FormStepProps } from '../FormStep/FormStep';
import FormFooter, { FormFooterProps } from '../FormFooter/FormFooter';
import FormHeader, { FormHeaderProps } from '../FormHeader/FormHeader';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { getValidationSchema } from '../fields/config/form-validation-schema';
import {
  EnvironmentContext,
  LoadingSpinner,
  notifications,
  redirectToSessionExpiredPage,
} from '@r-and-a-shared-ui/common-ui';
import {
  DEFAULT_VALUES_QUERY_KEY,
  KONTENT_ITEM_CODENAME_ATTRIBUTE_KEY,
  STORAGE,
} from '../constants';
import { FormContentContext } from '../context/FormContentContext';
import { FormProps } from '../types';
import {
  getDefaultValues,
  getDefaultValuesInitialFormState,
  setFormStateFromSessionStorage,
} from '../helpers/form-helpers';
import { userDefaultValuesUpdate } from '@r-and-a-shared-ui/randa-trpc-server/input-schemas';
import { getStepIndexKey, setSpecialFieldsValues } from '../helpers/form-steps-helper';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useActionContext } from '../context/useActionsContext';
import { RestrictedContentPrompt } from '@r-and-a-shared-ui/common-ui';
import {
  getCustomStateDecodedJSON,
  useAuthContext,
} from '@r-and-a-shared-ui/auth-client';

import styles from './Form.module.scss';
import { tryGetDictionaryItemValue } from '../../utils';

export function Form(props: FormProps) {
  const {
    formCodename,
    formHeaderData: defaultHeaderData,
    steps,
    formFooterData,
    hasReCaptcha,
    reCaptchaAttributeKey,
    errorsDictionary,
    successMessagesDictionary,
    nextStepsAfterSubmit,
    defaultValuesCallbackUrl,
    defaultValuesJson,
    bearerToken,
    isMembership,
    backgroundImage,
    toEmail,
    fromEmail,
    subject,
    emailKey,
  } = props;

  const [currentFormStep, setCurrentFormStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [submitSuccessMessage, setSubmitSuccessMessage] = useState('');
  const context = useActionContext();
  const { API } = context || {};
  const [allowedToFetch, setAllowedToFetch] = useState(false);
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);
  const stepIndexKey = getStepIndexKey(formCodename);

  const { Image } = useContext(EnvironmentContext);
  const [defValuesQueryRes, setDefValuesQueryRes] = useState(undefined);

  const {
    formState,
    formState: { errors },
    getValues,
    setValue,
    reset,
    ...methods
  } = useForm({
    resolver: zodResolver(getValidationSchema(steps)),
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: getDefaultValues({
      callbackUrl: defaultValuesCallbackUrl,
      hardcodedDefaultValues: userDefaultValuesUpdate,
      predefinedJSONObj: defaultValuesJson,
    }),
  });

  let isAuthenticated: boolean | null = false;
  let isAuthContextAvailable = false;

  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { userState } = useAuthContext();
    isAuthenticated = userState.isAuthenticated;
    isAuthContextAvailable = true;
  } catch (error) {
    // We are in SSO
  }

  const ssoUrl = isMembership ? '' : getSSOUrl();
  const baseUrl = ssoUrl || window.location.origin;

  const callBackUrl = ensureValidUri(defaultValuesCallbackUrl, baseUrl);

  const mutation = API?.[DEFAULT_VALUES_QUERY_KEY]?.useMutation();
  const defaultValuesAsync = mutation?.mutateAsync;

  useEffect(() => {
    if (
      allowedToFetch &&
      !!bearerToken &&
      !!callBackUrl &&
      !!defaultValuesAsync
    ) {
      defaultValuesAsync({ callBackUrl, token: bearerToken })
        .then((res: any) => {
          setDefValuesQueryRes(res);
        })
        .catch((err: any) => {
          console.error(err);
        });
    }
  }, [allowedToFetch, bearerToken, callBackUrl, defaultValuesAsync]);

  useEffect(() => {
    let defaultValues = undefined;
    if (mutation?.status === 'success' && defValuesQueryRes) {
      defaultValues = flattenObjectWithChar(defValuesQueryRes, '_');
    } else {
      if (hasValue(defaultValuesJson) && defaultValuesJson !== undefined)
        defaultValues = flattenObjectWithChar(
          JSON.parse(defaultValuesJson),
          '_',
        );
    }
    if (defaultValues !== undefined) {
      setSpecialFieldsValues(defaultValues, '_', steps);
      reset(defaultValues);
      setValue(KONTENT_ITEM_CODENAME_ATTRIBUTE_KEY, formCodename);
    }
  }, [defValuesQueryRes]);

  useEffect(() => {
    if (mutation?.status === 'success' || !defaultValuesCallbackUrl) {
      setAllowedToFetch(false);
    } else {
      setAllowedToFetch(true);
    }
  }, [mutation?.status, defaultValuesCallbackUrl]);

  useEffect(() => {
    setValue(KONTENT_ITEM_CODENAME_ATTRIBUTE_KEY, formCodename);
  }, [formCodename, setValue]);

  const handleReCaptchaVerify = useCallback(
    (token: string) => {
      if (!hasReCaptcha || !reCaptchaAttributeKey) {
        return;
      }

      setValue(reCaptchaAttributeKey, token);
    },
    [hasReCaptcha, reCaptchaAttributeKey, setValue],
  );

  const stepSetter = () => {
    const step = getNumberFromSessionStorage(stepIndexKey);
    const totalSteps = steps?.length || 0;

    if (currentFormStep >= totalSteps) {
      return;
    }

    setCurrentFormStep(step);
  };

  window.addEventListener(STORAGE, stepSetter);

  useEffect(() => {
    setNumberInSessionStorage(stepIndexKey, 0);
    setFormStateFromSessionStorage(setValue);
    window.dispatchEvent(new Event(STORAGE));
    return () => {
      deleteFromSessionStorage(stepIndexKey);
      window.removeEventListener(STORAGE, stepSetter);
    };
  }, [setValue]);

  const notifyError = (message: string) => notifications.errorSmall(message);
  const notifySuccess = (message: string) =>
    notifications.successSmall(message);

  const toggleRefreshReCaptcha = useCallback(() => {
    if (!hasReCaptcha) {
      return;
    }

    setRefreshReCaptcha(r => !r);
  }, [hasReCaptcha]);

  useEffect(() => {
    const errorMessage = errors?.root?.serverError?.message;
    if (!isNullOrUndefined(errorMessage)) {
      if (errorMessage === INVALID_AUTHORIZATION_CODE_ERROR_MESSAGE) {
        redirectToSessionExpiredPage();
      } else {
        notifyError(errorMessage);
        toggleRefreshReCaptcha();
      }
    }
  }, [errors?.root?.serverError, toggleRefreshReCaptcha]);

  useEffect(() => {
    if (submitSuccessMessage) {
      notifySuccess(submitSuccessMessage);
      toggleRefreshReCaptcha();
    }
  }, [submitSuccessMessage, toggleRefreshReCaptcha]);

  useEffect(() => {
    const customState = getCustomStateDecodedJSON();

    if (!customState || !hasValue(customState.confirmedEmail)) {
      return;
    }

    if (!customState.confirmedEmail) {
      const errorEmailConfirmation =
        tryGetDictionaryItemValue(errorsDictionary, EMAIL_CONFIRMATION_ERROR) ||
        ERROR_EMAIL_CONFIRMATION_MESSAGE;
      notifyError(errorEmailConfirmation);
      return;
    }

    const successEmailConfirmed =
      tryGetDictionaryItemValue(
        successMessagesDictionary,
        EMAIL_CONFIRMED_SUCCESS,
      ) || SUCCESS_EMAIL_CONFIRMED_MESSAGE;

    notifySuccess(successEmailConfirmed);
  }, []);

  const renderFormStep = (
    formStepProps: FormStepProps[] | undefined,
  ): React.JSX.Element | undefined => {
    if (
      !hasValue(formStepProps) ||
      typeof formStepProps === 'undefined' ||
      currentFormStep >= formStepProps.length
    ) {
      return undefined;
    }
    return (
      <FormStep
        {...formStepProps[currentFormStep]}
        key={formStepProps[currentFormStep]?.key}
        isMembership={isMembership}
        toEmail={toEmail}
        fromEmail={fromEmail}
        subject={subject}
        emailKey={emailKey}
      />
    );
  };

  const getHeaderDataToDisplay = (): FormHeaderProps | undefined => {
    const customHeader = steps
      ? steps[currentFormStep]?.customHeader
      : undefined;
    return customHeader || defaultHeaderData;
  };

  const renderFormHeader = (
    formHeaderData: FormHeaderProps | undefined,
  ): React.JSX.Element | undefined => {
    if (isNullOrUndefined(formHeaderData)) {
      return undefined;
    }
    return <FormHeader {...formHeaderData} isMembership={isMembership} />;
  };

  const renderFormFooter = (
    formFooterData: FormFooterProps | undefined,
  ): React.JSX.Element | undefined => {
    if (isNullOrUndefined(formFooterData)) {
      return undefined;
    }
    return <FormFooter {...formFooterData} isMembership={isMembership} />;
  };

  if (isAuthContextAvailable && isMembership && !isAuthenticated) {
    return <RestrictedContentPrompt isMembership={isMembership} />;
  }

  return (
    <FormContentContext.Provider
      value={{
        formContentProps: props,
        currentFormStep,
        isLoading,
        errorsDictionary,
        setSubmitSuccessMessage,
        successMessagesDictionary,
        nextStepsAfterSubmit,
        initialFormState: getDefaultValuesInitialFormState({
          queryRes: defValuesQueryRes,
          predefinedJSONObj: defaultValuesJson,
        }),
      }}
    >
      <FormProvider
        {...methods}
        reset={reset}
        getValues={getValues}
        setValue={setValue}
        formState={formState}
      >
        <form>
          {hasReCaptcha && (
            <GoogleReCaptcha
              onVerify={handleReCaptchaVerify}
              refreshReCaptcha={refreshReCaptcha}
            />
          )}
          <div
            className={`${styles.form} ${
              isMembership ? styles['form-membership'] : ''
            }`}
          >
            <div
              className={`${styles['form__wrapper']} ${
                isMembership ? styles['form-membership__wrapper'] : ''
              }`}
            >
              <div
                className={`${
                  isMembership ? styles['form-membership__content'] : ''
                }`}
              >
                {backgroundImage && (
                  <Image
                    src={backgroundImage}
                    alt="Form background image"
                    className={styles['form__background']}
                    width={300}
                    height={300}
                  />
                )}
                {renderFormHeader(getHeaderDataToDisplay())}
                {isLoading && <LoadingSpinner />}
                <div
                  className={`${styles['form__content--body']} ${
                    isLoading ? styles.loading : ''
                  }`}
                >
                  {renderFormStep(steps)}
                </div>
              </div>
              {renderFormFooter(formFooterData)}
            </div>
          </div>
        </form>
      </FormProvider>
    </FormContentContext.Provider>
  );
}

export default Form;
